import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Wrench, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { ProductThumbnail } from '@/components/ui/product-thumbnail';

const recipeSchema = z.object({
  finished_product_id: z.string().min(1, 'Please select a finished product'),
  materials: z.array(z.object({
    raw_material_id: z.string().min(1, 'Please select a raw material'),
    quantity_required: z.number().min(0.1, 'Quantity must be greater than 0')
  })).min(1, 'At least one raw material is required').max(6, 'Maximum 6 raw materials allowed')
});

type RecipeFormData = z.infer<typeof recipeSchema>;

export const ProductRecipes = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  
  const { data: recipes = [], isLoading } = useQuery({
    queryKey: ['product-recipes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_recipes')
        .select(`
          *,
          finished_product:inventory_items!finished_product_id(name, image_url),
          raw_material:inventory_items!raw_material_id(name, unit_of_measure, image_url)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const { data: inventoryItems = [] } = useQuery({
    queryKey: ['inventory-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('id, name, type, unit_of_measure')
        .order('name');
      
      if (error) throw error;
      return data;
    },
  });

  const finishedProducts = inventoryItems.filter(item => item.type === 'finished_product');
  const rawMaterials = inventoryItems.filter(item => item.type === 'raw_material');

  const form = useForm<RecipeFormData>({
    resolver: zodResolver(recipeSchema),
    defaultValues: {
      finished_product_id: '',
      materials: [{ raw_material_id: '', quantity_required: 1 }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'materials'
  });

  const createRecipeMutation = useMutation({
    mutationFn: async (data: RecipeFormData) => {
      const recipes = data.materials.map(material => ({
        finished_product_id: data.finished_product_id,
        raw_material_id: material.raw_material_id,
        quantity_required: material.quantity_required
      }));

      const { error } = await supabase
        .from('product_recipes')
        .insert(recipes);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-recipes'] });
      toast.success('Recipe created successfully');
      setIsDialogOpen(false);
      form.reset();
    },
    onError: (error) => {
      toast.error('Failed to create recipe: ' + error.message);
    }
  });

  const handleSubmit = (data: RecipeFormData) => {
    createRecipeMutation.mutate(data);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Product Recipes
            </CardTitle>
            <CardDescription>
              Bill of Materials for finished products
            </CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Recipe
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Recipe</DialogTitle>
                <DialogDescription>
                  Define the raw materials needed to create a finished product
                </DialogDescription>
              </DialogHeader>
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="finished_product_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Finished Product</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a finished product" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {finishedProducts.map((product) => (
                              <SelectItem key={product.id} value={product.id}>
                                {product.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="text-sm font-medium">Raw Materials</h4>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => append({ raw_material_id: '', quantity_required: 1 })}
                        disabled={fields.length >= 6}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Material
                      </Button>
                    </div>

                    {fields.map((field, index) => (
                      <div key={field.id} className="flex gap-4 items-end">
                        <FormField
                          control={form.control}
                          name={`materials.${index}.raw_material_id`}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormLabel>Raw Material</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select raw material" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {rawMaterials.map((material) => (
                                    <SelectItem key={material.id} value={material.id}>
                                      {material.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name={`materials.${index}.quantity_required`}
                          render={({ field }) => (
                            <FormItem className="w-32">
                              <FormLabel>Quantity</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0.1"
                                  {...field}
                                  onChange={(e) => field.onChange(parseFloat(e.target.value))}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {fields.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => remove(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createRecipeMutation.isPending}>
                      {createRecipeMutation.isPending ? 'Creating...' : 'Create Recipe'}
                    </Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Finished Product</TableHead>
              <TableHead>Raw Material</TableHead>
              <TableHead>Quantity Required</TableHead>
              <TableHead>Unit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center">Loading...</TableCell>
              </TableRow>
            ) : recipes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center">No recipes found</TableCell>
              </TableRow>
            ) : (
              recipes.map((recipe) => (
                <TableRow key={recipe.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <ProductThumbnail 
                        imageUrl={recipe.finished_product?.image_url} 
                        name={recipe.finished_product?.name || 'Unknown Product'} 
                        size="sm"
                      />
                      <span className="font-medium">
                        {recipe.finished_product?.name || 'Unknown Product'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <ProductThumbnail 
                        imageUrl={recipe.raw_material?.image_url} 
                        name={recipe.raw_material?.name || 'Unknown Material'} 
                        size="sm"
                      />
                      {recipe.raw_material?.name || 'Unknown Material'}
                    </div>
                  </TableCell>
                  <TableCell>{recipe.quantity_required}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {recipe.raw_material?.unit_of_measure || 'pieces'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};